// ========= START: src/hooks/useSpeechRecognition.js (Complete, Updated File) =========

import { useState, useEffect, useRef } from 'react';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const recognition = SpeechRecognition ? new SpeechRecognition() : null;

if (recognition) {
  recognition.continuous = true;
  recognition.lang = 'en-US';
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;
}

export const useSpeechRecognition = () => {
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const recognitionRef = useRef(recognition);

  useEffect(() => {
    const rec = recognitionRef.current;
    if (!rec) {
      console.error("Speech Recognition is not supported in this browser.");
      return;
    }

    rec.onstart = () => {
      setIsListening(true);
    };

    rec.onend = () => {
      setIsListening(false);
    };

    rec.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech' && event.error !== 'audio-capture') {
        setIsListening(false);
        rec.stoppedByApp = true;
      }
    };

    rec.onresult = (event) => {
      let interimTranscript = '';
      let collectedFinalTranscript = ''; // Use a different variable name to avoid shadowing
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const currentTranscript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          collectedFinalTranscript += currentTranscript;
        } else {
          interimTranscript += currentTranscript;
        }
      }
      
      if (collectedFinalTranscript) {
        console.log('Final transcript received from API:', collectedFinalTranscript);
        setFinalTranscript(collectedFinalTranscript);
        setTranscript(collectedFinalTranscript);
      } else if (interimTranscript) {
        setTranscript(interimTranscript);
      }
    };

    return () => {
      if (rec) {
        rec.stoppedByApp = true;
        rec.onstart = null;
        rec.onend = null;
        rec.onerror = null;
        rec.onresult = null;
        try {
          rec.stop();
        } catch (e) {
          // Ignore error if recognition wasn't running
        }
      }
    };
  }, []);

    const startListening = () => {
      if (recognitionRef.current) {
        recognitionRef.current.stoppedByApp = false;
        setTranscript('');
        setFinalTranscript('');
        if (!isListening) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.warn('Speech recognition already running, continuing with existing session');
          }
        }
      }
    };
    
    const stopListening = () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current.stoppedByApp = true;
        } catch (e) {
          console.warn('Error stopping speech recognition:', e);
        }
      }
    };

    // THIS IS THE NEW FUNCTION WE ARE ADDING
    // It allows App.js to clear the transcript after processing it.
    const resetTranscript = () => {
        setTranscript('');
        setFinalTranscript('');
    };

  return {
    transcript,
    finalTranscript,
    isListening,
    startListening,
    stopListening,
    hasRecognitionSupport: !!recognition,
    resetTranscript, // EXPORT THE NEW FUNCTION so App.js can use it
  };
};

// ========= END: src/hooks/useSpeechRecognition.js (Complete, Updated File) =========